import * as React from 'react';
import styled from 'styled-components';

import { getColor, getHeight, getShadow, getWidth } from '@process-advisor/process-miner/shared/theme';
import { ColorPickerContext, type ColorPickerContextType } from '@process-advisor/process-miner/shared/providers/colorPickerProvider';
import { calculateXPosition, calculateYPosition } from '@process-advisor/process-miner/components/colorPicker/utils';
import { useClickOutside } from '@process-advisor/process-miner/shared/hooks/useClickOutside';
import { ColorsGrid } from '@process-advisor/process-miner/components/colorPicker/colorsGrid';
import { CustomColorPicker } from '@process-advisor/process-miner/components/colorPicker/customColorPicker';

type ColorPickerContainer = {
    open: boolean;
    top: string;
    left: string;
}

const Container = styled.div<ColorPickerContainer>`
    width: ${getWidth('colorPicker')}px;
    height: ${getHeight('colorPicker')}px;
    position: absolute;
    top: ${({ top }) => top};
    left: ${({ left }) => left};
    border-radius: 8px;
    padding: 10px 10px 0;
    overflow: hidden;
    z-index: 9999;
    background-color: ${getColor('white')};
    box-shadow: ${getShadow('panel')};
    display: ${({ open }) => open ? 'block' : 'none'};
    transition: .2s;
`;

export const ColorPicker: React.FC = () => {
    const {
        props,
        setProps,
    } = React.useContext<ColorPickerContextType>(ColorPickerContext);
    const [
        prevOnClose,
        setPrevOnClose,
    ] = React.useState<() => void>(props.onClose);
    const [prevShow, setPrevShow] = React.useState<boolean>(props.show);
    const pickerRef = React.useRef();
    const { x, y, show, color } = props;

    const hidePicker = () => setProps({ ...props, show: false });
    useClickOutside(pickerRef, hidePicker);

    const onColorChange = (color) => setProps({ ...props, color });

    React.useEffect(() => {
        if (prevShow) {
            prevOnClose && prevOnClose();
            setPrevOnClose(props.onClose);
        }
    }, [props.id]);

    React.useEffect(() => {
        if (!props.show) {
            props.onClose && props.onClose();
        }
        setPrevShow(props.show);
    }, [props.show]);

    return (
        <Container
            ref={pickerRef}
            top={calculateYPosition(y)}
            left={calculateXPosition(x)}
            open={show}>
            <CustomColorPicker
                onColorChange={onColorChange}
                activeColor={color} />
            <ColorsGrid onColorChange={onColorChange} />
        </Container>
    );
};
